import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { showSuccess, showError } from '@/lib/toast';
import {
  Key, Plus, Copy, Check, ShieldAlert, Trash2, Clock,
  AlertTriangle, Shield,
} from 'lucide-react';

interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  createdAt: string;
  expiresAt: string | null;
  active: boolean;
}

const API_SCOPES = [
  { key: 'instances.read', labelKey: 'apiKeys.scopeTypes.instancesRead', group: 'Instances' },
  { key: 'instances.write', labelKey: 'apiKeys.scopeTypes.instancesWrite', group: 'Instances' },
  { key: 'billing.read', labelKey: 'apiKeys.scopeTypes.billingRead', group: 'Billing' },
  { key: 'monitoring.read', labelKey: 'apiKeys.scopeTypes.monitoringRead', group: 'Monitoring' },
  { key: 'channels.read', labelKey: 'apiKeys.scopeTypes.channelsRead', group: 'Channels' },
  { key: 'channels.write', labelKey: 'apiKeys.scopeTypes.channelsWrite', group: 'Channels' },
];

export function ApiKeys() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Create form state
  const [newName, setNewName] = useState('');
  const [newScopes, setNewScopes] = useState<string[]>([]);
  const [newExpiration, setNewExpiration] = useState('');

  // Query
  const { data: keysData, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: async () => {
      const { data } = await api.get('/api-keys');
      return data;
    },
  });

  const keys: ApiKey[] = keysData?.keys || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        name: newName,
        scopes: newScopes,
      };
      if (newExpiration) {
        payload.expiresAt = newExpiration;
      }
      const { data } = await api.post('/api-keys', payload);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      showSuccess(t('apiKeys.createSuccess'));
      setCreatedKey(data.key);
      setNewName('');
      setNewScopes([]);
      setNewExpiration('');
    },
    onError: () => {
      showError(t('apiKeys.createError'));
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api-keys/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      showSuccess(t('apiKeys.revokeSuccess'));
      setRevokeTarget(null);
    },
    onError: () => {
      showError(t('apiKeys.revokeError'));
    },
  });

  const toggleScope = (scopeKey: string) => {
    setNewScopes((prev) =>
      prev.includes(scopeKey)
        ? prev.filter((s) => s !== scopeKey)
        : [...prev, scopeKey],
    );
  };

  const handleCopyKey = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
      showSuccess(t('apiKeys.keyCopied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showError(t('apiKeys.copyError'));
    }
  };

  const handleCloseCreate = (open: boolean) => {
    if (!open) {
      setCreateOpen(false);
      setCreatedKey(null);
      setCopied(false);
    }
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return t('apiKeys.never');
    return new Date(dateStr).toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <Key className="h-5 w-5 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {t('apiKeys.title')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('apiKeys.subtitle')}
            </p>
          </div>
        </div>
        <Button
          className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="h-4 w-4" />
          {t('apiKeys.createKey')}
        </Button>
      </div>

      {/* Security warning banner */}
      <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20">
        <CardContent className="flex items-start gap-3 p-4">
          <ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              {t('apiKeys.securityNoticeTitle')}
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-0.5">
              {t('apiKeys.securityNoticeBody')}
            </p>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Loading state */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : keys.length === 0 ? (
        /* Empty state */
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-20">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Key className="h-8 w-8 text-muted-foreground/40" />
            </div>
            <h3 className="text-lg font-medium mb-2">
              {t('apiKeys.noKeys')}
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              {t('apiKeys.noKeysDescription')}
            </p>
          </CardContent>
        </Card>
      ) : (
        /* Key list */
        <div className="space-y-4">
          {keys.map((key) => (
            <Card
              key={key.id}
              className={cn(
                'group transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/5',
                key.active
                  ? 'hover:border-emerald-200 dark:hover:border-emerald-800'
                  : 'opacity-60 hover:opacity-80',
              )}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1 space-y-3">
                    {/* Name and status */}
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                        <Shield className="h-4 w-4 text-emerald-500" />
                      </div>
                      <div>
                        <h3 className="font-medium text-sm">{key.name}</h3>
                        <p className="font-mono text-xs text-muted-foreground mt-0.5">
                          {key.prefix}...
                        </p>
                      </div>
                      <Badge
                        className={cn(
                          'text-[11px] ml-2',
                          key.active
                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : 'bg-red-100 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400',
                        )}
                      >
                        {key.active
                          ? t('apiKeys.active')
                          : t('apiKeys.revoked')}
                      </Badge>
                    </div>

                    {/* Scopes */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {key.scopes.map((scope) => (
                        <Badge
                          key={scope}
                          variant="outline"
                          className="text-[11px] font-mono"
                        >
                          {scope}
                        </Badge>
                      ))}
                    </div>

                    {/* Meta */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {t('apiKeys.lastUsedLabel')} {formatDate(key.lastUsedAt)}
                      </span>
                      <span>
                        {t('apiKeys.createdLabel')} {formatDate(key.createdAt)}
                      </span>
                      {key.expiresAt && (
                        <span className="flex items-center gap-1">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          {t('apiKeys.expiresLabel')} {formatDate(key.expiresAt)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0">
                    {key.active && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 gap-1 text-xs"
                        onClick={() => setRevokeTarget(key)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {t('apiKeys.revoke')}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create API Key Dialog */}
      <Dialog open={createOpen} onOpenChange={handleCloseCreate}>
        <DialogContent className="sm:max-w-lg">
          {createdKey ? (
            /* Key created - show key */
            <>
              <DialogHeader>
                <DialogTitle className="text-emerald-600">
                  {t('apiKeys.keyCreatedTitle')}
                </DialogTitle>
                <DialogDescription>
                  {t('apiKeys.keyCreatedDescription')}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Warning */}
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    {t('apiKeys.keyCloseWarning')}
                  </p>
                </div>

                {/* Key display */}
                <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <code className="font-mono text-sm break-all flex-1 text-emerald-800 dark:text-emerald-300">
                      {createdKey}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        'gap-1 flex-shrink-0 transition-all',
                        copied && 'border-emerald-300 text-emerald-600',
                      )}
                      onClick={handleCopyKey}
                    >
                      {copied ? (
                        <>
                          <Check className="h-3.5 w-3.5" />
                          {t('apiKeys.copied')}
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" />
                          {t('apiKeys.copy')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleCloseCreate(false)}
                >
                  {t('apiKeys.done')}
                </Button>
              </div>
            </>
          ) : (
            /* Create form */
            <>
              <DialogHeader>
                <DialogTitle>{t('apiKeys.createKey')}</DialogTitle>
                <DialogDescription>
                  {t('apiKeys.createDescription')}
                </DialogDescription>
              </DialogHeader>

              <Separator />

              <div className="space-y-4">
                {/* Name */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t('apiKeys.name')}
                  </label>
                  <Input
                    placeholder={t('apiKeys.namePlaceholder')}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </div>

                {/* Scopes */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t('apiKeys.scopesLabel')}
                  </label>
                  <div className="border rounded-lg p-3 space-y-2">
                    {API_SCOPES.map((scope) => (
                      <label
                        key={scope.key}
                        className="flex items-center gap-3 cursor-pointer py-1 px-2 rounded-md hover:bg-muted/50 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={newScopes.includes(scope.key)}
                          onChange={() => toggleScope(scope.key)}
                          className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="text-sm">
                          {t(scope.labelKey)}
                        </span>
                        <span className="text-xs font-mono text-muted-foreground ml-auto">
                          {scope.key}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Expiration */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t('apiKeys.expirationLabel')}
                  </label>
                  <Input
                    type="date"
                    value={newExpiration}
                    onChange={(e) => setNewExpiration(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('apiKeys.expirationHelp')}
                  </p>
                </div>

                <Button
                  className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20"
                  onClick={() => createMutation.mutate()}
                  disabled={!newName || newScopes.length === 0 || createMutation.isPending}
                >
                  <Key className="h-4 w-4" />
                  {createMutation.isPending
                    ? t('apiKeys.creating')
                    : t('apiKeys.createKey')}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <Dialog open={!!revokeTarget} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {t('apiKeys.revokeTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('apiKeys.revokeDescription')}
            </DialogDescription>
          </DialogHeader>

          {revokeTarget && (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30 p-3 space-y-1">
              <p className="font-medium text-sm">{revokeTarget.name}</p>
              <p className="font-mono text-xs text-muted-foreground">{revokeTarget.prefix}...</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              className="gap-2"
              onClick={() => revokeTarget && revokeMutation.mutate(revokeTarget.id)}
              disabled={revokeMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
              {revokeMutation.isPending
                ? t('apiKeys.revoking')
                : t('apiKeys.revokeKey')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
